import { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Progress } from "./ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  ArrowRight,
  Upload,
  FileSpreadsheet,
  Plus,
  X,
  AlertCircle,
  Trash2,
  Edit,
  CheckCircle2,
  Download,
  Calendar,
  TrendingUp,
  LogOut
} from "lucide-react";
import { Badge } from "./ui/badge";


// Account types
const ACCOUNT_TYPES = {
  BROKERAGE: "Brokerage",
  CASH_MANAGEMENT: "Cash Management",
  TRADITIONAL_IRA: "Traditional IRA",
  ROTH_IRA: "Roth IRA",
  SEP_IRA: "SEP IRA",
  SIMPLE_IRA: "SIMPLE IRA",
  TRADITIONAL_401K: "Traditional 401(k)",
  ROTH_401K: "Roth 401(k)",
  TRADITIONAL_403B: "Traditional 403(b)",
  ROTH_403B: "Roth 403(b)",
  TRADITIONAL_457: "Traditional 457",
  PLAN_529: "529 College Savings",
  COVERDELL_ESA: "Coverdell ESA",
  HSA: "Health Savings Account",
  INHERITED_IRA: "Inherited IRA",
  ROLLOVER_IRA: "Rollover IRA",
  TRUST: "Trust Account",
};

const OWNERSHIP_TYPES = {
  INDIVIDUAL: "Individual",
  JOINT_WROS: "Joint - Right of Survivorship",
  JOINT_TIC: "Joint - Tenants in Common",
  TRUST: "Trust",
  ENTITY_LLC: "Entity - LLC",
  ENTITY_CORP: "Entity - Corporation",
  ENTITY_PARTNERSHIP: "Entity - Partnership",
  CUSTODIAL_UTMA: "Custodial - UTMA",
  CUSTODIAL_UGMA: "Custodial - UGMA",
};

const SECTORS = [
  "Technology",
  "Healthcare",
  "Finance",
  "Real Estate",
  "Energy",
  "Consumer Goods",
  "Industrials",
  "Commodities",
  "Diversified",
  "Fixed Income",
  "Growth",
  "International",
  "Financial Services",
];

// Sample data for table display (10 rows)
const SAMPLE_CSV_DATA = [
  { accountName: "Main Investment Account", accountType: "BROKERAGE",custodian: "Fidelity", accountNumberCustodian: "****6789", ownershipType: "INDIVIDUAL", symbol: "AAPL", name: "Apple Inc", cashBalance: 5000.00, shares: 500, avgCost: 150.00, sector: "Technology",ytdReturn: 10.5 },
  { accountName: "Main Investment Account", accountType: "BROKERAGE",custodian: "Fidelity", accountNumberCustodian: "****6789", ownershipType: "INDIVIDUAL", symbol: "MSFT", name: "Microsoft Corp", cashBalance: 12000.00, shares: 400, avgCost: 300.00, sector: "Technology",ytdReturn: 8.2 },
  { accountName: "Main Investment Account", accountType: "BROKERAGE",custodian: "Fidelity", accountNumberCustodian: "****6789", ownershipType: "INDIVIDUAL", symbol: "JNJ", name: "Johnson & Johnson", cashBalance: 3200.00, shares: 200, avgCost: 160.00, sector: "Healthcare",ytdReturn: 5.1 },
  { accountName: "Roth IRA - Primary", accountType: "ROTH_IRA", custodian: "Vanguard", accountNumberCustodian: "****3421", ownershipType: "INDIVIDUAL", symbol: "VTI", name: "Vanguard Total Stock Market ETF", cashBalance: 9450.00, shares: 450, avgCost: 210.00, sector: "Diversified",ytdReturn: 12.3},
  { accountName: "Roth IRA - Primary", accountType: "ROTH_IRA", custodian: "Vanguard", accountNumberCustodian: "****3421", ownershipType: "INDIVIDUAL", symbol: "BND", name: "Vanguard Total Bond Market ETF", cashBalance: 1200.00, shares: 150, avgCost: 80.00, sector: "Fixed Income",ytdReturn: 3.4 },
  { accountName: "Employer 401(k)", accountType: "TRADITIONAL_401K", custodian: "Fidelity", accountNumberCustodian: "****8902", ownershipType: "INDIVIDUAL", symbol: "SPY", name: "SPDR S&P 5₀₀ ETF Trust", cashBalance: 21000.00, shares: 500, avgCost: 420.00, sector: "Diversified",ytdReturn: 9.8 },
  { accountName: "Employer 401(k)", accountType: "TRADITIONAL_401K", custodian: "Fidelity", accountNumberCustodian: "****8902", ownershipType: "INDIVIDUAL", symbol: "QQQ", name: "Invesco QQQ Trust", cashBalance: 4550.00, shares: 130, avgCost: 350.00, sector: "Technology",ytdReturn: 11.2 },
  { accountName: "Joint Investment Account", accountType: "BROKERAGE", custodian: "Charles Schwab", accountNumberCustodian: "****9988", ownershipType: "JOINT_WROS", symbol: "VNQ", name: "Vanguard Real Estate ETF", cashBalance: 7200.00, shares: 800, avgCost: 90.00, sector: "Real Estate",ytdReturn: 6.7 },
  { accountName: "Joint Investment Account", accountType: "BROKERAGE", custodian: "Charles Schwab", accountNumberCustodian: "****9988", ownershipType: "JOINT_WROS", symbol: "NVDA", name: "NVIDIA Corporation", cashBalance: 9000.00, shares: 200, avgCost: 450.00, sector: "Technology",ytdReturn: 15.4 },
  { accountName: "Traditional IRA", accountType: "TRADITIONAL_IRA", custodian: "Fidelity", accountNumberCustodian: "****4455", ownershipType: "INDIVIDUAL", symbol: "VOO", name: "Vanguard S&P 500 ETF", cashBalance: 1400.00, shares: 350, avgCost: 400.00, sector: "Diversified",ytdReturn: 7.6 },
];

const SAMPLE_CSV_DATA_STATEMENT = [
  { statementDate: "2025-01-31", startDate: "2025-01-01", endDate: "2025-01-31", periodType: "MONTHLY", startingValue: 478000.00, endingValue: 485000.00, deposits: 5000.00, withdrawals: 0, fees: 150.00, dividendsEarned: 1200.00, interestEarned: 45.00, netReturn: 6900.00, timeWeightedReturn: 1.45, moneyWeightedReturn: 1.42 },
  { statementDate: "2025-02-28", startDate: "2025-02-01", endDate: "2025-02-28", periodType: "MONTHLY", startingValue: 485000.00, endingValue: 492000.00, deposits: 3000.00, withdrawals: 2000.00, fees: 150.00, dividendsEarned: 1100.00, interestEarned: 40.00, netReturn: 7010.00, timeWeightedReturn: 1.38, moneyWeightedReturn: 1.35 },
  { statementDate: "2025-03-31", startDate: "2025-03-01", endDate: "2025-03-31", periodType: "MONTHLY", startingValue: 492000.00, endingValue: 501000.00, deposits: 4000.00, withdrawals: 1000.00, fees: 150.00, dividendsEarned: 1300.00, interestEarned: 50.00, netReturn: 7800.00, timeWeightedReturn: 1.52, moneyWeightedReturn: 1.48 },
];

export function PortfolioUploadStep({
  progressPercentage,
  portfolioAccounts,
  onSaveAccount,
  onUpdateAccount,
  onDeleteAccount,
  //onSaveStatement,
  //onUpdateStatement,
  //onDeleteStatement,
  onUpdate,
  onBack,
  onContinue,
  onLogout,
}) {
  const [uploadError, setUploadError] = useState(null);
  const [editingAccount, setEditingAccount] = useState(null);
  const [isAddingAccount, setIsAddingAccount] = useState(false);
  const [isUpdatingAccount, setIsUpdatingAccount] = useState(false);
  const [currentAccount, setCurrentAccount] = useState({
    id: null,
    accountType: "BROKERAGE",
    ownershipType: "INDIVIDUAL",
    accountName: "",
    accountNumber: "",
    custodian: "",
    accountNumberCustodian: "",
    balance: "",
    cashBalance: "",
    ytdReturn: "",
    contributions: "",
    openedDate: "",
    status: "Active",
    positions: [],
    statements: [], // Add statements array to account
  });
  const [manualPosition, setManualPosition] = useState({
    symbol: "",
    name: "",
    shares: "",
    avgCost: "",
    sector: "",
  });
  
  // Portfolio Statement State - now with account selection
  const [selectedAccountNumber, setSelectedAccountNumber] = useState(null);
  const [statementUploadError, setStatementUploadError] = useState(null);
  const [isAddingStatement, setIsAddingStatement] = useState(false);
  const [currentStatement, setCurrentStatement] = useState({
    statementDate: "",
    startDate: "",
    endDate: "",
    periodType: "MONTHLY",
    startingValue: "",
    endingValue: "",
    deposits: "",
    withdrawals: "",
    fees: "",
    dividendsEarned: "",
    interestEarned: "",
  });

  // Generate unique ID for accounts
  const generateAccountNumber = () => {
    return `account_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const generateStatementId = () => {
    return `stmt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Check if statement date already exists in account
  const isStatementDateDuplicate = (accountNumber, statementDate) => {
    const account = (portfolioAccounts || []).find(acc => acc.accountNumber === accountNumber);
    if (!account || !account.statements) return false;
    return account.statements.some(s => s.statementDate === statementDate);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          setUploadError("File appears to be empty or invalid");
          return;
        }

        // Parse CSV (expecting: Account Name, Account Type, Account Number, Ownership Type, Symbol, Name, Shares, Avg Cost, Sector)
        const accountsMap = {};
        
        for (let i = 1; i < lines.length; i++) {
          const parts = lines[i].split(',').map(p => p.trim());
          if (parts.length >= 9) {
            const accountKey = parts[0] + "_" + parts[2]; // Use account name + number as key
            
            if (!accountsMap[accountKey]) {
              accountsMap[accountKey] = {
                id: null,
                accountName: parts[0],
                accountType: parts[1] || "BROKERAGE",
                accountNumber: generateAccountNumber(),
                ownershipType: parts[3] || "INDIVIDUAL",
                status: "Active",
                positions: [],
                statements: [], // Initialize statements array
              };
            }
            
            accountsMap[accountKey].positions.push({
              symbol: parts[4],
              name: parts[5],
              shares: parseFloat(parts[6]) || 0,
              avgCost: parseFloat(parts[7]) || 0,
              sector: parts[8] || "Diversified",
            });
          }
        }

        const accounts = Object.values(accountsMap);
        
        if (accounts.length === 0) {
          setUploadError("No valid accounts found in file");
          return;
        }

        onUpdate([...(portfolioAccounts || []), ...accounts]);
        setUploadError(null);
      } catch (error) {
        setUploadError("Error parsing file. Please check the format.");
      }
    };
    reader.readAsText(file);
  };

  const handleAddPosition = () => {
    if (!manualPosition.symbol || !manualPosition.shares || !manualPosition.avgCost) {
      return;
    }

    const newPosition = {
      symbol: manualPosition.symbol.toUpperCase(),
      name: manualPosition.name || manualPosition.symbol,
      shares: parseFloat(manualPosition.shares),
      avgCost: parseFloat(manualPosition.avgCost),
      sector: manualPosition.sector || "Diversified",
    };

    setCurrentAccount({
      ...currentAccount,
      positions: [...currentAccount.positions, newPosition],
    });
    
    setManualPosition({ symbol: "", name: "", shares: "", avgCost: "", sector: "" });
  };

  const handleRemovePosition = (index) => {
    setCurrentAccount({
      ...currentAccount,
      positions: currentAccount.positions.filter((_, i) => i !== index),
    });
  };

  const handleSaveAccount = () => {
    if (!currentAccount.accountName || currentAccount.positions.length === 0) {
      setUploadError("Please provide account name and at least one position");
      return;
    }

    if (!currentAccount.custodian || currentAccount.custodian.length === 0) {
      setUploadError("Please provide a custodian for the account");
      return;
    }

    if (!currentAccount.accountNumberCustodian || currentAccount.accountNumberCustodian.length === 0) {
      setUploadError("Please provide a custodian account number for the account");
      return;
    }


    const accountToSave = {
      ...currentAccount,
      id: null,
      accountNumber: generateAccountNumber(),
      balance: parseFloat(currentAccount.balance) || undefined,
      cashBalance: parseFloat(currentAccount.cashBalance) || undefined,
      ytdReturn: parseFloat(currentAccount.ytdReturn) || undefined,
      contributions: parseFloat(currentAccount.contributions) || undefined,
      statements: currentAccount.statements || [], // Preserve existing statements
    };

    if (editingAccount !== null) {
      const updated = [...(portfolioAccounts || [])];
      updated[editingAccount] = accountToSave;
      onUpdate(updated);
      setEditingAccount(null);
    } else {
      onUpdate([...(portfolioAccounts || []), accountToSave]);
    }

    setIsAddingAccount(false);
    setCurrentAccount({
      id: null,
      accountType: "BROKERAGE",
      ownershipType: "INDIVIDUAL",
      accountName: "",
      accountNumber: "",
      custodian: "",
      accountNumberCustodian: "",
      balance: "",
      cashBalance: "",
      ytdReturn: "",
      contributions: "",
      openedDate: "",
      status: "Active",
      positions: [],
      statements: [],
    });
    setUploadError(null);
  };

  const handleSaveAccountWorking = async () => {
    if (!currentAccount.accountName || currentAccount.positions.length === 0) {
      setUploadError("Please provide account name and at least one position");
      return;
    }

    const accountToSave = {
      ...currentAccount,
      id: null,
      accountNumber: generateAccountNumber(),
      balance: parseFloat(currentAccount.balance) || undefined,
      cashBalance: parseFloat(currentAccount.cashBalance) || undefined,
      ytdReturn: parseFloat(currentAccount.ytdReturn) || undefined,
      contributions: parseFloat(currentAccount.contributions) || undefined,
      statements: currentAccount.statements || [], // Preserve existing statements
    };

    try {
      let success = false
      if(isAddingAccount && !isUpdatingAccount){
        setIsAddingAccount(false);
        setIsUpdatingAccount(false);
        success = await onSaveAccount(accountToSave);
      }else if(isAddingAccount && isUpdatingAccount){
        setIsAddingAccount(false);
        setIsUpdatingAccount(false);
        success = await onSaveAccount(accountToSave); 
        success = await onUpdateAccount (accountToSave);
      }
      if (success) {
        // Reset form or show success message
        setCurrentAccount({
          accountType: "BROKERAGE",
          ownershipType: "INDIVIDUAL",
          accountName: "",
          accountNumber: "",
          custodian: "",
          accountNumberCustodian: "",
          balance: "",
          cashBalance: "",
          ytdReturn: "",
          contributions: "",
          openedDate: "",
          status: "Active",
          positions: [],
        });
      }
      //setIsAddingAccount(false);
    } catch (error) {
      console.error('Error saving account:', error);
    }

    if (editingAccount !== null) {
      const updated = [...(portfolioAccounts || [])];
      updated[editingAccount] = accountToSave;
      onUpdate(updated);
      setEditingAccount(null);
    } else {
      onUpdate([...(portfolioAccounts || []), accountToSave]);
    }

    setIsAddingAccount(false);
    setCurrentAccount({
      id: null,
      accountType: "BROKERAGE",
      ownershipType: "INDIVIDUAL",
      accountName: "",
      accountNumber: "",
      custodian: "",
      accountNumberCustodian: "",
      balance: "",
      ytdReturn: "",
      contributions: "",
      openedDate: "",
      status: "Active",
      positions: [],
      statements: [],
    });
    setUploadError(null);
  };

  const handleSaveNewAccount = async () => {
    if (!currentAccount.accountName || currentAccount.positions.length === 0) {
      setUploadError("Please provide account name and at least one position");
      return;
    }
    const accountToSave = {
      ...currentAccount,
      id: null,
      accountNumber: generateAccountNumber(),
    };
    try {
      const success = await onSaveAccount(accountToSave);
      if (success) {
        // Reset form or show success message
        setCurrentAccount({
          accountType: "BROKERAGE",
          ownershipType: "INDIVIDUAL",
          accountName: "",
          accountNumber: "",
          custodian: "",
          accountNumberCustodian: "",
          balance: "",
          ytdReturn: "",
          contributions: "",
          openedDate: "",
          status: "Active",
          positions: [],
        });
      }
    } catch (error) {
      console.error('Error saving account:', error);
    }
  };

  const handleEditAccount = (index) => {
    setEditingAccount(index);
    setCurrentAccount(portfolioAccounts[index]);
    setIsAddingAccount(true);
    setIsUpdatingAccount(true);
  };

  const handleRemoveAccount = (index) => {
    onUpdate((portfolioAccounts || []).filter((_, i) => i !== index));
  };

  // Portfolio Statement Handlers
  const handleStatementFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!portfolioAccounts || portfolioAccounts.length === 0) {
      setStatementUploadError("Please add at least one account before uploading statements");
      return;
    }

    if (!selectedAccountNumber) {
      setStatementUploadError("Please select an account before uploading statements");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          setStatementUploadError("File appears to be empty or invalid");
          return;
        }

        const statements = [];
        const duplicateDates = [];
        
        for (let i = 1; i < lines.length; i++) {
          const parts = lines[i].split(',').map(p => p.trim());
          if (parts.length >= 3) {
            const statementDate = parts[0];
            
            if (isStatementDateDuplicate(selectedAccountNumber, statementDate)) {
              duplicateDates.push(statementDate);
              continue;
            }
            
            statements.push({
              id: null,
              accountId: selectedAccountNumber,
              statementDate: statementDate,
              startDate: parts[1] || "",
              endDate: parts[2] || "",
              periodType: parts[3] || "MONTHLY",
              startingValue: parseFloat(parts[4]) || 0,
              endingValue: parseFloat(parts[5]) || 0,
              deposits: parseFloat(parts[6]) || 0,
              withdrawals: parseFloat(parts[7]) || 0,
              fees: parseFloat(parts[8]) || 0,
              dividendsEarned: parseFloat(parts[9]) || 0,
              interestEarned: parseFloat(parts[10]) || 0,
              netReturn: parseFloat(parts[11]) || 0,
              timeWeightedReturn: parseFloat(parts[12]) || 0,
              moneyWeightedReturn: parseFloat(parts[13]) || 0,
            });
          }
        }

        if (statements.length === 0) {
          if (duplicateDates.length > 0) {
            setStatementUploadError(`All statements skipped. Duplicate dates found: ${duplicateDates.join(', ')}`);
          } else {
            setStatementUploadError("No valid statements found in file");
          }
          return;
        }

        const updatedAccounts = (portfolioAccounts || []).map(account => {
          if (account.accountNumber === selectedAccountNumber) {
            return {
              ...account,
              statements: [...(account.statements || []), ...statements]
            };
          }
          return account;
        });

        onUpdate(updatedAccounts);
        
        if (duplicateDates.length > 0) {
          setStatementUploadError(`Added ${statements.length} statement(s). Skipped ${duplicateDates.length} duplicate(s): ${duplicateDates.join(', ')}`);
        } else {
          setStatementUploadError(null);
        }
      } catch (error) {
        setStatementUploadError("Error parsing file. Please check the format.");
      }
    };
    reader.readAsText(file);
  };

  const handleAddStatement = () => {
    if (!portfolioAccounts || portfolioAccounts.length === 0) {
      setStatementUploadError("Please add at least one account before adding statements");
      return;
    }

    if (!selectedAccountNumber) {
      setStatementUploadError("Please select an account before adding statements");
      return;
    }

    if (!currentStatement.statementDate || !currentStatement.endingValue) {
      setStatementUploadError("Statement date and ending value are required");
      return;
    }

    if (isStatementDateDuplicate(selectedAccountNumber, currentStatement.statementDate)) {
      setStatementUploadError(`A statement for ${currentStatement.statementDate} already exists in this account`);
      return;
    }

    const newStatement = {
      id: null,
      accountId: selectedAccountNumber,
      statementDate: currentStatement.statementDate,
      startDate: currentStatement.startDate || currentStatement.statementDate,
      endDate: currentStatement.endDate || currentStatement.statementDate,
      periodType: currentStatement.periodType,
      startingValue: parseFloat(currentStatement.startingValue) || 0,
      endingValue: parseFloat(currentStatement.endingValue) || 0,
      deposits: parseFloat(currentStatement.deposits) || 0,
      withdrawals: parseFloat(currentStatement.withdrawals) || 0,
      fees: parseFloat(currentStatement.fees) || 0,
      dividendsEarned: parseFloat(currentStatement.dividendsEarned) || 0,
      interestEarned: parseFloat(currentStatement.interestEarned) || 0,
      netReturn: currentStatement.endingValue && currentStatement.startingValue
        ? ((parseFloat(currentStatement.endingValue) - parseFloat(currentStatement.startingValue)) / parseFloat(currentStatement.startingValue)) * 100
        : 0,
      timeWeightedReturn: 0,
      moneyWeightedReturn: 0,
    };

    const updatedAccounts = (portfolioAccounts || []).map(account => {
      if (account.accountNumber === selectedAccountNumber) {
        return {
          ...account,
          statements: [...(account.statements || []), newStatement]
        };
      }
      return account;
    });

    onUpdate(updatedAccounts);
    setCurrentStatement({
      statementDate: "",
      startDate: "",
      endDate: "",
      periodType: "MONTHLY",
      startingValue: "",
      endingValue: "",
      deposits: "",
      withdrawals: "",
      fees: "",
      dividendsEarned: "",
      interestEarned: "",
    });
    setIsAddingStatement(false);
    setStatementUploadError(null);
  };

  const handleRemoveStatement = (accountId, statementIndex) => {
    const updatedAccounts = (portfolioAccounts || []).map(account => {
      if (account.accountNumber === accountId) {
        return {
          ...account,
          statements: (account.statements || []).filter((_, i) => i !== statementIndex)
        };
      }
      return account;
    });
    onUpdate(updatedAccounts);
  };

  // Get total statement count across all accounts
  const totalStatementCount = (portfolioAccounts || []).reduce((sum, account) => {
    return sum + (account.statements ? account.statements.length : 0);
  }, 0);

  const totalAccountValue = (portfolioAccounts || []).reduce((sum, account) => {
    const accountTotal = account.positions.reduce(
      (posSum, pos) => posSum + (pos.shares * pos.avgCost),
      0
    );
    return sum + accountTotal;
  }, 0);

  const totalPositions = (portfolioAccounts || []).reduce(
    (sum, account) => sum + account.positions.length,
    0
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <Card className="max-w-6xl w-full max-h-[90vh] overflow-y-auto relative">
        {/* Logout Button */}
        {onLogout && (
          <div className="absolute top-4 right-4 z-10">
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="text-muted-foreground hover:text-destructive"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        )}
        <CardHeader>
          <Progress value={progressPercentage} className="mb-4" />
          <CardTitle>Upload Your Portfolio</CardTitle>
          <CardDescription>
            Add your accounts and holdings to get personalized insights (Optional)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Tabs for Upload Methods */}
          <Tabs defaultValue="manual" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="manual" className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add Account Manually
              </TabsTrigger>
              <TabsTrigger value="csv" className="flex items-center gap-2" disabled>
                <Upload className="h-4 w-4" />
                Upload CSV File (coming soon)
              </TabsTrigger>
            </TabsList>

            {/* CSV Upload Tab */}
            <TabsContent value="csv" className="space-y-4 mt-4">
              <div className="flex items-center justify-between px-1">
                <h3 className="text-sm font-medium">Upload Portfolio CSV</h3>
                <a
                  href="/sample-portfolio-upload.csv"
                  download
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  <Download className="h-3 w-3" />
                  Download Sample CSV
                </a>
              </div>
              
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors">
                <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <Label htmlFor="csv-upload" className="cursor-pointer">
                  <div className="space-y-2">
                    <p>Click to upload or drag and drop</p>
                    <p className="text-sm text-muted-foreground">
                      CSV file with all your accounts and positions
                    </p>
                  </div>
                  <Input
                    id="csv-upload"
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                </Label>
              </div>

              {uploadError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{uploadError}</span>
                </div>
              )}

              {/* Sample CSV Data Table (10 rows) */}
              <div className="bg-muted rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium">Sample CSV Data Format (10 Rows)</p>
                
                <div className="bg-background rounded-lg overflow-hidden border">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium whitespace-nowrap border-r">Account Name</th>
                          <th className="text-left p-2 font-medium whitespace-nowrap border-r">Custodian</th>
                          <th className="text-left p-2 font-medium whitespace-nowrap border-r">Custodian Account #</th>
                          <th className="text-left p-2 font-medium whitespace-nowrap border-r">Account Type</th>
                          <th className="text-left p-2 font-medium whitespace-nowrap border-r">Ownership Type</th>
                          <th className="text-left p-2 font-medium whitespace-nowrap border-r">Cash Balance</th>
                          <th className="text-left p-2 font-medium whitespace-nowrap border-r">YTD Return(%)</th>
                          <th className="text-left p-2 font-medium whitespace-nowrap border-r">Symbol</th>
                          <th className="text-left p-2 font-medium whitespace-nowrap border-r">Name</th>
                          <th className="text-left p-2 font-medium whitespace-nowrap border-r">Sector</th>
                          <th className="text-right p-2 font-medium whitespace-nowrap border-r">Shares</th>
                          <th className="text-right p-2 font-medium whitespace-nowrap border-r">Avg Cost</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {SAMPLE_CSV_DATA.map((row, idx) => (
                          <tr key={idx} className="hover:bg-muted/30">
                            <td className="p-2 whitespace-nowrap border-r">{row.accountName}</td>
                            <td className="p-2 whitespace-nowrap border-r">{row.custodian}</td>
                            <td className="p-2 whitespace-nowrap border-r">{row.accountNumberCustodian}</td>
                            <td className="p-2 whitespace-nowrap border-r">{row.accountType}</td>
                            <td className="p-2 whitespace-nowrap border-r">{row.ownershipType}</td>
                            <td className="p-2 whitespace-nowrap border-r">${row.cashBalance.toFixed(2)}</td>
                            <td className="p-2 whitespace-nowrap border-r">{row.ytdReturn.toFixed(2)}%</td>
                            <td className="p-2 whitespace-nowrap font-medium border-r">{row.symbol}</td>
                            <td className="p-2 whitespace-nowrap border-r">{row.name}</td>
                            <td className="p-2 whitespace-nowrap">{row.sector}</td>
                            <td className="p-2 text-right whitespace-nowrap border-r">{row.shares}</td>
                            <td className="p-2 text-right whitespace-nowrap border-r">${row.avgCost.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="space-y-2 text-xs text-muted-foreground">
                  <p className="font-medium text-foreground">CSV Format Requirements:</p>
                  <ul className="space-y-1 pl-4 list-disc">
                    <li>First row must be headers (as shown above)</li>
                    <li>Each subsequent row represents one position in an account</li>
                    <li>Positions with same Account Name + Account Number are grouped together</li>
                    <li>Use comma (,) to separate columns</li>
                    <li>Numeric values should not include $ or commas</li>
                  </ul>
                </div>
              </div>
            </TabsContent>

            {/* Manual Entry Tab */}
            <TabsContent value="manual" className="space-y-6 mt-4">
              {!isAddingAccount ? (
                <div className="text-center py-8">
                  <div className="h-16 w-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4">
                    <Plus className="h-8 w-8" />
                  </div>
                  <h3 className="text-lg mb-2">Add Your First Account</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Enter account details and positions manually
                  </p>
                  <Button onClick={() => setIsAddingAccount(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Account
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Account Information */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium">Account Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="accountName">Account Name *</Label>
                        <Input
                          id="accountName"
                          placeholder="Main Investment Account"
                          value={currentAccount.accountName}
                          onChange={(e) =>
                            setCurrentAccount({ ...currentAccount, accountName: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="accountNumber">Account Number</Label>
                        <Input
                          id="accountNumber"
                          placeholder="Auto-generated"
                          value={currentAccount.accountNumber}
                          disabled
                          className="bg-muted"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="custodian">Custodian</Label>
                        <Input
                          id="custodian"
                          placeholder="Fidelity"
                          value={currentAccount.custodian}
                          onChange={(e) =>
                            setCurrentAccount({ ...currentAccount, custodian: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="accountNumberCustodian">Custodian Account #</Label>
                        <Input
                          id="accountNumberCustodian"
                          placeholder="XYZ123"
                          value={currentAccount.accountNumberCustodian}
                          onChange={(e) =>
                            setCurrentAccount({ ...currentAccount, accountNumberCustodian: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="accountType">Account Type *</Label>
                        <Select
                          value={currentAccount.accountType}
                          onValueChange={(value) =>
                            setCurrentAccount({ ...currentAccount, accountType: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(ACCOUNT_TYPES).map(([key, label]) => (
                              <SelectItem key={key} value={key}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ownershipType">Ownership Type *</Label>
                        <Select
                          value={currentAccount.ownershipType}
                          onValueChange={(value) =>
                            setCurrentAccount({ ...currentAccount, ownershipType: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select ownership" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(OWNERSHIP_TYPES).map(([key, label]) => (
                              <SelectItem key={key} value={key}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="balance">Current Balance</Label>
                        <Input
                          id="balance"
                          type="number"
                          placeholder="100000"
                          value={currentAccount.balance}
                          onChange={(e) =>
                            setCurrentAccount({ ...currentAccount, balance: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cashBalance">Cash Balance</Label>
                        <Input
                          id="cashBalance"
                          type="number"
                          placeholder="50000"
                          value={currentAccount.cashBalance}
                          onChange={(e) =>
                            setCurrentAccount({ ...currentAccount, cashBalance: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ytdReturn">YTD Return (%)</Label>
                        <Input
                          id="ytdReturn"
                          type="number"
                          step="0.1"
                          placeholder="12.5"
                          value={currentAccount.ytdReturn}
                          onChange={(e) =>
                            setCurrentAccount({ ...currentAccount, ytdReturn: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* Positions */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium">Add Positions</h3>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="symbol">Symbol *</Label>
                        <Input
                          id="symbol"
                          placeholder="AAPL"
                          value={manualPosition.symbol}
                          onChange={(e) =>
                            setManualPosition({ ...manualPosition, symbol: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="name">Company Name</Label>
                        <Input
                          id="name"
                          placeholder="Apple Inc"
                          value={manualPosition.name}
                          onChange={(e) =>
                            setManualPosition({ ...manualPosition, name: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="sector">Sector</Label>
                        <Select
                          value={manualPosition.sector}
                          onValueChange={(value) =>
                            setManualPosition({ ...manualPosition, sector: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select sector" />
                          </SelectTrigger>
                          <SelectContent>
                            {SECTORS.map((sector) => (
                              <SelectItem key={sector} value={sector}>
                                {sector}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="shares">Shares *</Label>
                        <Input
                          id="shares"
                          type="number"
                          placeholder="100"
                          value={manualPosition.shares}
                          onChange={(e) =>
                            setManualPosition({ ...manualPosition, shares: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="avgCost">Avg Cost (per share) *</Label>
                        <Input
                          id="avgCost"
                          type="number"
                          step="0.01"
                          placeholder="150.00"
                          value={manualPosition.avgCost}
                          onChange={(e) =>
                            setManualPosition({ ...manualPosition, avgCost: e.target.value })
                          }
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          onClick={handleAddPosition}
                          disabled={
                            !manualPosition.symbol ||
                            !manualPosition.shares ||
                            !manualPosition.avgCost
                          }
                          className="w-full"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Position
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Positions List */}
                  {currentAccount.positions.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium">
                        Positions ({currentAccount.positions.length})
                      </h3>
                      <div className="border rounded-lg overflow-hidden">
                        <div className="max-h-48 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-muted sticky top-0">
                              <tr>
                                <th className="text-left p-2">Symbol</th>
                                <th className="text-left p-2">Name</th>
                                <th className="text-left p-2">Sector</th>
                                <th className="text-right p-2">Shares</th>
                                <th className="text-right p-2">Avg Cost</th>
                                <th className="text-right p-2">Total</th>
                                <th className="w-10"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {currentAccount.positions.map((position, index) => (
                                <tr key={index} className="border-t">
                                  <td className="p-2">{position.symbol}</td>
                                  <td className="p-2 text-muted-foreground">{position.name}</td>
                                  <td className="p-2">
                                    <Badge variant="outline" className="text-xs">
                                      {position.sector}
                                    </Badge>
                                  </td>
                                  <td className="p-2 text-right">{position.shares}</td>
                                  <td className="p-2 text-right">${position.avgCost.toFixed(2)}</td>
                                  <td className="p-2 text-right">
                                    ${(position.shares * position.avgCost).toFixed(2)}
                                  </td>
                                  <td className="p-2">
                                    <button
                                      onClick={() => handleRemovePosition(index)}
                                      className="text-muted-foreground hover:text-destructive"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {uploadError && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>{uploadError}</span>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsAddingAccount(false);
                        setEditingAccount(null);
                        setCurrentAccount({
                          id: null,
                          accountType: "BROKERAGE",
                          ownershipType: "INDIVIDUAL",
                          accountName: "",
                          accountNumber: "",
                          custodian: "",
                          accountNumberCustodian: "",
                          balance: "",
                          ytdReturn: "",
                          contributions: "",
                          openedDate: "",
                          status: "Active",
                          positions: [],
                          statements: [],
                        });
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleSaveAccount} className="flex-1">
                      {editingAccount !== null ? "Update Account" : "Save Account"}
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Portfolio Statements Section */}
          <div className="space-y-4 pt-6 border-t mt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Upload Account Statements
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Add account statements to track portfolio performance over time
                </p>
              </div>
            </div>

            {/* Account Selection Dropdown */}
            {portfolioAccounts && portfolioAccounts.length > 0 && (
              <div className="space-y-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Label htmlFor="statementAccount" className="text-sm font-medium flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-blue-600" />
                  Select Account for Statements *
                </Label>
                <Select
                  value={selectedAccountNumber || ""}
                  onValueChange={setSelectedAccountNumber}
                >
                  <SelectTrigger id="statementAccount">
                    <SelectValue placeholder="Choose an account to add statements..." />
                  </SelectTrigger>
                  <SelectContent>
                    {portfolioAccounts.map((account) => (
                       <SelectItem key={account.accountNumber} value={account.accountNumber}>
                        {account.accountName} - {account.accountNumber} ({account.accountType})
                        {account.statements && account.statements.length > 0 && (
                          <span className="text-xs text-muted-foreground ml-2">
                            ({account.statements.length} statement{account.statements.length !== 1 ? 's' : ''})
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-blue-700 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Statements will be added to the selected account only. Each account maintains its own statement history.
                </p>
              </div>
            )}

            {/* Warning banner when no accounts exist */}
            {(!portfolioAccounts || portfolioAccounts.length === 0) && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                <span className="text-xs text-amber-700">
                  Please add at least one account before uploading statements
                </span>
              </div>
            )}

            <Tabs defaultValue="statement-manual" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger 
                  value="statement-manual" 
                  className="flex items-center gap-2"
                  disabled={!portfolioAccounts || portfolioAccounts.length === 0 || !selectedAccountNumber}
                >
                  <Calendar className="h-4 w-4" />
                  Add Statement Manually
                </TabsTrigger>
                <TabsTrigger 
                  value="statement-csv" 
                  className="flex items-center gap-2"
                  disabled
                >
                  <Upload className="h-4 w-4" />
                  Upload Statement CSV (coming soon)
                </TabsTrigger>
              </TabsList>

              {/* CSV Upload Tab for Statements */}
              <TabsContent value="statement-csv" className="space-y-4 mt-4">
                <div className="flex items-center justify-between px-1">
                  <h3 className="text-sm font-medium">Upload Account Statements CSV</h3>
                </div>
                
                <div className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  (!portfolioAccounts || portfolioAccounts.length === 0 || !selectedAccountNumber) 
                    ? 'border-muted bg-muted/30 cursor-not-allowed' 
                    : 'border-border hover:border-primary cursor-pointer'
                }`}>
                  <TrendingUp className={`h-12 w-12 mx-auto mb-4 ${
                    (!portfolioAccounts || portfolioAccounts.length === 0 || !selectedAccountNumber) 
                      ? 'text-muted-foreground/50' 
                      : 'text-muted-foreground'
                  }`} />
                  <Label 
                    htmlFor="statement-csv-upload" 
                    className={(!portfolioAccounts || portfolioAccounts.length === 0 || !selectedAccountNumber) ? 'cursor-not-allowed' : 'cursor-pointer'}
                  >
                    <div className="space-y-2">
                      <p>Click to upload account statements CSV</p>
                      <p className="text-sm text-muted-foreground">
                        CSV file with statement data
                      </p>
                      {!selectedAccountNumber && portfolioAccounts && portfolioAccounts.length > 0 && (
                        <p className="text-xs text-amber-600 font-medium">
                          Please select an account first
                        </p>
                      )}
                    </div>
                    <Input
                      id="statement-csv-upload"
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={handleStatementFileUpload}
                      disabled={!portfolioAccounts || portfolioAccounts.length === 0 || !selectedAccountNumber}
                    />
                  </Label>
                </div>

                {statementUploadError && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{statementUploadError}</span>
                  </div>
                )}

                {/* Sample CSV Format */}
                <div className="bg-muted rounded-lg p-4 space-y-3">
                  <p className="text-sm font-medium">Sample CSV Data Format</p>
                  
                  <div className="bg-background rounded-lg overflow-hidden border">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-medium whitespace-nowrap border-r">Statement Date</th>
                            <th className="text-left p-2 font-medium whitespace-nowrap border-r">Start Date</th>
                            <th className="text-left p-2 font-medium whitespace-nowrap border-r">End Date</th>
                            <th className="text-left p-2 font-medium whitespace-nowrap border-r">Period</th>
                            <th className="text-right p-2 font-medium whitespace-nowrap border-r">Start Value</th>
                            <th className="text-right p-2 font-medium whitespace-nowrap border-r">End Value</th>
                            <th className="text-right p-2 font-medium whitespace-nowrap border-r">Deposits</th>
                            <th className="text-right p-2 font-medium whitespace-nowrap border-r">Withdrawals</th>
                            <th className="text-right p-2 font-medium whitespace-nowrap border-r">Fees</th>
                            <th className="text-right p-2 font-medium whitespace-nowrap border-r">Dividends</th>
                            <th className="text-right p-2 font-medium whitespace-nowrap">Interest</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {SAMPLE_CSV_DATA_STATEMENT.map((row, idx) => (
                            <tr key={idx} className="hover:bg-muted/30">
                              <td className="p-2 whitespace-nowrap border-r">{row.statementDate}</td>
                              <td className="p-2 whitespace-nowrap border-r">{row.startDate}</td>
                              <td className="p-2 whitespace-nowrap border-r">{row.endDate}</td>
                              <td className="p-2 whitespace-nowrap border-r">{row.periodType}</td>
                              <td className="p-2 text-right whitespace-nowrap border-r">{row.startingValue.toFixed(2)}</td>
                              <td className="p-2 text-right whitespace-nowrap border-r">{row.endingValue.toFixed(2)}</td>
                              <td className="p-2 text-right whitespace-nowrap border-r">{row.deposits.toFixed(2)}</td>
                              <td className="p-2 text-right whitespace-nowrap border-r">{row.withdrawals.toFixed(2)}</td>
                              <td className="p-2 text-right whitespace-nowrap border-r">{row.fees.toFixed(2)}</td>
                              <td className="p-2 text-right whitespace-nowrap border-r">{row.dividendsEarned.toFixed(2)}</td>
                              <td className="p-2 text-right whitespace-nowrap">{row.interestEarned.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="space-y-2 text-xs text-muted-foreground">
                    <p className="font-medium text-foreground">CSV Format Requirements:</p>
                    <ul className="space-y-1 pl-4 list-disc">
                      <li>First row must be headers (as shown above)</li>
                      <li>Statement Date, Start Date, End Date format: YYYY-MM-DD</li>
                      <li>Starting Value and Ending Value are required fields</li>
                      <li>Numeric values should not include $ or commas</li>
                    </ul>
                  </div>
                </div>
              </TabsContent>

              {/* Manual Entry Tab for Statements */}
              <TabsContent value="statement-manual" className="space-y-6 mt-4">
                {!isAddingStatement ? (
                  <div className="text-center py-8">
                    <div className={`h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                      (!portfolioAccounts || portfolioAccounts.length === 0 || !selectedAccountNumber)
                        ? 'bg-muted text-muted-foreground'
                        : 'bg-blue-100 text-blue-600'
                    }`}>
                      <Calendar className="h-8 w-8" />
                    </div>
                    <h3 className={`text-lg mb-2 ${(!portfolioAccounts || portfolioAccounts.length === 0 || !selectedAccountNumber) ? 'text-muted-foreground' : ''}`}>
                      Add Account Statement
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Record a statement with starting and ending values
                    </p>
                    {!selectedAccountNumber && portfolioAccounts && portfolioAccounts.length > 0 && (
                      <p className="text-xs text-amber-600 font-medium mb-4">
                        Please select an account first
                      </p>
                    )}
                    <Button 
                      onClick={() => setIsAddingStatement(true)}
                      disabled={!portfolioAccounts || portfolioAccounts.length === 0 || !selectedAccountNumber}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Statement
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="stmtDate">Statement Date *</Label>
                        <Input
                          id="stmtDate"
                          type="date"
                          value={currentStatement.statementDate}
                          onChange={(e) =>
                            setCurrentStatement({ ...currentStatement, statementDate: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stmtPeriodType">Period Type</Label>
                        <Select
                          value={currentStatement.periodType}
                          onValueChange={(value) =>
                            setCurrentStatement({ ...currentStatement, periodType: value })
                          }
                        >
                          <SelectTrigger id="stmtPeriodType">
                            <SelectValue placeholder="Select period" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="MONTHLY">Monthly</SelectItem>
                            <SelectItem value="QUARTERLY">Quarterly</SelectItem>
                            <SelectItem value="ANNUAL">Annual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stmtStartDate">Start Date</Label>
                        <Input
                          id="stmtStartDate"
                          type="date"
                          value={currentStatement.startDate}
                          onChange={(e) =>
                            setCurrentStatement({ ...currentStatement, startDate: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stmtEndDate">End Date</Label>
                        <Input
                          id="stmtEndDate"
                          type="date"
                          value={currentStatement.endDate}
                          onChange={(e) =>
                            setCurrentStatement({ ...currentStatement, endDate: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stmtStartingValue">Starting Value *</Label>
                        <Input
                          id="stmtStartingValue"
                          type="number"
                          step="0.01"
                          placeholder="480000.00"
                          value={currentStatement.startingValue}
                          onChange={(e) =>
                            setCurrentStatement({ ...currentStatement, startingValue: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stmtEndingValue">Ending Value *</Label>
                        <Input
                          id="stmtEndingValue"
                          type="number"
                          step="0.01"
                          placeholder="485000.00"
                          value={currentStatement.endingValue}
                          onChange={(e) =>
                            setCurrentStatement({ ...currentStatement, endingValue: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stmtDeposits">Deposits</Label>
                        <Input
                          id="stmtDeposits"
                          type="number"
                          step="0.01"
                          placeholder="0"
                          value={currentStatement.deposits}
                          onChange={(e) =>
                            setCurrentStatement({ ...currentStatement, deposits: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stmtWithdrawals">Withdrawals</Label>
                        <Input
                          id="stmtWithdrawals"
                          type="number"
                          step="0.01"
                          placeholder="0"
                          value={currentStatement.withdrawals}
                          onChange={(e) =>
                            setCurrentStatement({ ...currentStatement, withdrawals: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stmtFees">Fees</Label>
                        <Input
                          id="stmtFees"
                          type="number"
                          step="0.01"
                          placeholder="0"
                          value={currentStatement.fees}
                          onChange={(e) =>
                            setCurrentStatement({ ...currentStatement, fees: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stmtDividends">Dividends Earned</Label>
                        <Input
                          id="stmtDividends"
                          type="number"
                          step="0.01"
                          placeholder="0"
                          value={currentStatement.dividendsEarned}
                          onChange={(e) =>
                            setCurrentStatement({ ...currentStatement, dividendsEarned: e.target.value })
                          }
                        />
                      </div>
                    </div>

                    {statementUploadError && (
                      <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{statementUploadError}</span>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setIsAddingStatement(false);
                          setCurrentStatement({
                            statementDate: "",
                            startDate: "",
                            endDate: "",
                            periodType: "MONTHLY",
                            startingValue: "",
                            endingValue: "",
                            deposits: "",
                            withdrawals: "",
                            fees: "",
                            dividendsEarned: "",
                            interestEarned: "",
                          });
                          setStatementUploadError(null);
                        }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleAddStatement} 
                        className="flex-1"
                        disabled={
                          !portfolioAccounts || 
                          portfolioAccounts.length === 0 || 
                          !selectedAccountNumber ||
                          !currentStatement.statementDate || 
                          !currentStatement.endingValue
                        }
                      >
                        Add Statement
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Statements Preview - Grouped by Account */}
            {portfolioAccounts && portfolioAccounts.some(acc => acc.statements && acc.statements.length > 0) && (
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium">
                    Account Statements ({totalStatementCount} total)
                  </h3>
                  {!isAddingStatement && selectedAccountNumber && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsAddingStatement(true)}
                      disabled={!portfolioAccounts || portfolioAccounts.length === 0 || !selectedAccountNumber}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Another Statement
                    </Button>
                  )}
                </div>

                {portfolioAccounts.filter(acc => acc.statements && acc.statements.length > 0).map((account, accIndex) => (
                  <div key={account.id || accIndex} className="space-y-2">
                    <div className="flex items-center gap-2 px-2 py-1 bg-blue-50 rounded">
                      <Badge variant="outline" className="text-xs">
                        {account.accountName} - {account.accountNumber}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {account.statements.length} statement{account.statements.length !== 1 ? 's' : ''}
                      </span>
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                      <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted sticky top-0">
                            <tr>
                              <th className="text-left p-2">Date</th>
                              <th className="text-right p-2">Start Value</th>
                              <th className="text-right p-2">End Value</th>
                              <th className="text-right p-2">Deposits</th>
                              <th className="text-right p-2">Withdrawals</th>
                              <th className="w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {account.statements.map((statement, stmtIndex) => (
                              <tr key={stmtIndex} className="border-t hover:bg-muted/30">
                                <td className="p-2">{statement.statementDate}</td>
                                <td className="p-2 text-right">
                                  ${statement.startingValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                                <td className="p-2 text-right">
                                  ${statement.endingValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                                <td className="p-2 text-right">
                                  ${statement.deposits.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                                <td className="p-2 text-right">
                                  ${statement.withdrawals.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                                <td className="p-2">
                                  <button
                                    onClick={() => handleRemoveStatement(account.accountNumber, stmtIndex)}
                                    className="text-muted-foreground hover:text-destructive"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Accounts Preview */}
          {portfolioAccounts && portfolioAccounts.length > 0 && (
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium">
                    Your Accounts ({portfolioAccounts.length})
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {totalPositions} positions • Total Value: $
                    {totalAccountValue.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
                {!isAddingAccount && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsAddingAccount(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Another Account
                  </Button>
                )}
              </div>

              <div className="space-y-3">
                {portfolioAccounts.map((account, index) => {
                  const accountValue = account.positions.reduce(
                    (sum, pos) => sum + pos.shares * pos.avgCost,
                    0
                  );
                  return (
                    <Card key={index}>
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <CardTitle className="text-base">
                              {account.accountName}
                            </CardTitle>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Badge variant="outline">
                                {ACCOUNT_TYPES[account.accountType] || account.accountType}
                              </Badge>
                              <span>•</span>
                              <span>{account.positions.length} positions</span>
                              {account.accountNumber && (
                                <>
                                  <span>•</span>
                                  <span>{account.accountNumber}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditAccount(index)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveAccount(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="text-xl font-semibold mb-3">
                          ${accountValue.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </div>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {account.positions.map((position, posIndex) => (
                            <div
                              key={posIndex}
                              className="flex items-center justify-between text-xs py-1 border-t first:border-0"
                            >
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{position.symbol}</span>
                                <span className="text-muted-foreground">{position.name}</span>
                              </div>
                              <div className="text-right">
                                <div>{position.shares} shares</div>
                                <div className="text-muted-foreground">
                                  ${(position.shares * position.avgCost).toFixed(2)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={onBack}>
              Back
            </Button>
            <Button onClick={onContinue}>
              {portfolioAccounts && portfolioAccounts.length > 0
                ? "Continue"
                : "Skip for Now"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
